// bycursor-start
/**
 * 测量按钮文本的实际渲染宽度
 * 使用类似 isOverTwoLines 的方法，通过创建临时元素并测量其宽度
 * @param text 按钮文本
 * @returns 文本的渲染宽度（像素）
 * @author bycursor
 */
export const measureButtonTextWidth = (text: string): number => {
  if (!text) return 0;

  // 创建一个临时的测量元素，类似 isOverTwoLines 中使用 Range API 的方式
  const measureElement = document.createElement('span');
  measureElement.style.position = 'absolute';
  measureElement.style.visibility = 'hidden';
  measureElement.style.whiteSpace = 'nowrap';
  measureElement.style.fontSize = '15px'; // Dialog 按钮默认字号（从样式文件中获取）
  measureElement.style.fontFamily = 'inherit';
  measureElement.style.pointerEvents = 'none';
  measureElement.textContent = text;

  document.body.appendChild(measureElement);

  // 使用 Range API 获取文本的实际渲染宽度，类似 isOverTwoLines 的实现
  const range = document.createRange();
  range.selectNodeContents(measureElement);
  const rects = range.getClientRects();
  const width = rects.length > 0 ? rects[0].width : measureElement.offsetWidth;

  document.body.removeChild(measureElement);

  return width;
};

/**
 * 判断按钮文本是否过长
 * 使用类似 isOverTwoLines 的方法，通过实际测量文本宽度来判断
 * @param text 按钮文本
 * @returns 是否过长
 * @author bycursor
 */
export const isButtonTextTooLong = (text?: string): boolean => {
  if (!text) return false;

  // Dialog 宽度为 295px，两个按钮水平排列时，每个按钮可用宽度约为 130px（考虑分隔线和 padding）
  // 如果文本宽度超过 120px，则认为过长
  const maxButtonWidth = 120;
  const textWidth = measureButtonTextWidth(text);

  return textWidth > maxButtonWidth;
};

/**
 * 判断是否需要使用竖状排列
 * @param btnList 按钮列表
 * @param confirmButtonText 确认按钮文本
 * @param cancelButtonText 取消按钮文本
 * @returns 是否需要竖状排列
 * @author bycursor
 */
export const shouldUseVerticalLayout = (
  btnList?: Array<{ text: string; isPrimary?: boolean; onClick: () => void }>,
  confirmButtonText?: string,
  cancelButtonText?: string
): boolean => {
  // 如果传入了 btnList，自动使用竖状排列
  if (btnList && btnList.length > 0) {
    return true;
  }
  // 如果仅有 ConfirmBtn，没有 CancelBtn，则直接返回 false
  if (confirmButtonText && !cancelButtonText) {
    return false;
  }
  // 如果确认按钮或取消按钮文本过长，使用竖状排列
  if (isButtonTextTooLong(confirmButtonText) || isButtonTextTooLong(cancelButtonText)) {
    return true;
  }
  return false;
};

/**
 * 判断元素内容是否超过两行
 * 优先使用 Range API 方法获取实际行数，失败则使用高度测量方法
 * @param element HTML 元素
 * @returns 是否超过两行
 * @author bycursor
 */
export const isOverTwoLines = (element: HTMLElement): boolean => {
  if (!element || !element.firstChild) {
    return false;
  }

  // 优先尝试使用 Range API 方法（适用于纯字符串和包含文本的 ReactNode）
  try {
    const range = document.createRange();
    range.selectNodeContents(element);
    const rects = range.getClientRects();

    if (rects.length > 0) {
      // 对于 Range API，需要过滤掉重复的行（同一行的多个 rect 可能有相同的 top 值）
      // 通过比较 rect 的 top 值来统计实际行数，使用更宽松的容差（允许 2px 误差）
      const uniqueLines: number[] = [];
      for (const rect of rects) {
        const { top } = rect;
        // 检查是否已经存在相似的行（容差 2px）
        const isDuplicate = uniqueLines.some((existingTop) => Math.abs(existingTop - top) < 2);
        if (!isDuplicate) {
          uniqueLines.push(top);
        }
      }

      // 如果实际行数 > 2，说明超过两行（三行及以上）
      // uniqueLines.length === 2 表示正好两行，应该返回 false
      if (uniqueLines.length > 2) {
        return true;
      }
      // 如果 Range API 能准确计算出行数（1行或2行），直接返回结果
      if (uniqueLines.length <= 2) {
        return false;
      }
    }
  } catch (e) {
    // Range API 失败时，继续使用高度测量方法
  }

  // 如果 Range API 方法不可用或返回空结果，使用高度测量方法
  // 适用于包含多种子元素（文本、span、链接等）的情况
  const actualHeight = element.getBoundingClientRect().height;
  if (actualHeight === 0) {
    return false;
  }

  // 获取计算样式
  const computedStyle = window.getComputedStyle(element);
  const elementWidth = element.getBoundingClientRect().width;

  // 创建临时元素用于测量单行高度
  // 使用span元素，保持相同的字体样式和宽度
  const tempElement = document.createElement('span');
  tempElement.style.position = 'absolute';
  tempElement.style.visibility = 'hidden';
  tempElement.style.width = `${elementWidth}px`;
  tempElement.style.whiteSpace = 'nowrap';
  tempElement.style.overflow = 'hidden';
  // 复制关键样式属性
  tempElement.style.fontSize = computedStyle.fontSize;
  tempElement.style.fontFamily = computedStyle.fontFamily;
  tempElement.style.lineHeight = computedStyle.lineHeight;
  tempElement.style.fontWeight = computedStyle.fontWeight;
  tempElement.style.fontStyle = computedStyle.fontStyle;
  tempElement.style.letterSpacing = computedStyle.letterSpacing;
  tempElement.style.boxSizing = computedStyle.boxSizing;
  tempElement.style.padding = computedStyle.padding;

  // 获取元素中的第一个可见文本字符用于测量
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

  let firstChar = 'A'; // 默认字符
  let node: Node | null = walker.nextNode();
  while (node) {
    const text = (node as Text).textContent?.trim();
    if (text && text.length > 0) {
      firstChar = text.charAt(0);
      break;
    }
    node = walker.nextNode();
  }

  tempElement.textContent = firstChar;
  document.body.appendChild(tempElement);
  const singleLineHeight = tempElement.getBoundingClientRect().height;
  document.body.removeChild(tempElement);

  // 如果测量失败，尝试从计算样式获取行高
  let lineHeight = singleLineHeight;
  if (lineHeight === 0 || Number.isNaN(lineHeight)) {
    const lineHeightValue = computedStyle.lineHeight;
    if (lineHeightValue && lineHeightValue !== 'normal') {
      lineHeight = parseFloat(lineHeightValue);
      // 如果是相对单位（如 1.5），需要乘以字体大小
      if (lineHeight < 10) {
        const fontSize = parseFloat(computedStyle.fontSize) || 16;
        lineHeight *= fontSize;
      }
    } else {
      // 默认行高为字体大小的1.2倍
      const fontSize = parseFloat(computedStyle.fontSize) || 16;
      lineHeight = fontSize * 1.2;
    }
  }

  // 计算行数，需要更精确地判断
  // 考虑 padding 的影响，使用更精确的阈值
  const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
  const contentHeight = actualHeight - paddingTop - paddingBottom;
  const lines = contentHeight / lineHeight;

  // 使用更精确的阈值：2.5
  // 两行内容：lines 应该在 2.0-2.3 左右（考虑误差、子元素 margin 等）
  // 三行内容：lines 应该在 3.0-3.3 左右
  // 阈值设为 2.5 可以更准确地区分两行和三行，避免两行内容被误判
  return lines > 2.5;
};
// bycursor-end
// Contains cursor-generated edits.
